import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';

export interface ReportRow {
  agent_uuid: string;
  agent_db_id: number;
  agent_id: string;
  agent_name: string;
  client_name: string;
  product_type: string;
  total_pds: number;
  pd_duration_seconds?: number;
  pd_duration_hours: number;
  /** ISO date of the agent's most recent PD session in range, or null. */
  last_pd_date?: string | null;
  /** PD outcome counts for this agent in range; absent when the backend predates them. */
  pass_count?: number;
  review_count?: number;
  fail_count?: number;
}

export interface ReportSessionRow {
  id: number;
  session_code: string | null;
  customer_name: string | null;
  loan_type: string | null;
  status: string;
  result: string | null;
  score: number | null;
  created_at: string;
  duration_seconds?: number;
  duration_hours: number;
}

export interface ReportDetailSummary extends ReportRow {
  pass_count: number;
  review_count: number;
  fail_count: number;
  completed_count: number;
}

export interface AgentActivityDetailPayload {
  summary: ReportDetailSummary;
  sessions: {
    items: ReportSessionRow[];
    total: number;
    page: number;
    page_size: number;
    last_page: number;
  };
}

/** Totals over the whole filtered set (every page), not just the visible one. */
export interface ReportSummary {
  agents: number;
  total_pds: number;
  pass_count: number;
  review_count: number;
  fail_count: number;
}

export interface AgentActivityReportPayload {
  items: ReportRow[];
  total: number;
  page: number;
  page_size: number;
  last_page: number;
  summary?: ReportSummary;
}

export interface ListAgentActivityQuery {
  search?: string;
  product_type?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

@Injectable({ providedIn: 'root' })
export class ReportsApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl;

  listAgentActivity(query: ListAgentActivityQuery = {}): Observable<ApiEnvelope<AgentActivityReportPayload>> {
    let params = new HttpParams();
    if (query.search) params = params.set('search', query.search);
    if (query.product_type) params = params.set('product_type', query.product_type);
    if (query.date_from) params = params.set('date_from', query.date_from);
    if (query.date_to) params = params.set('date_to', query.date_to);
    if (query.page != null) params = params.set('page', String(query.page));
    if (query.page_size != null) params = params.set('page_size', String(query.page_size));
    return this.http.get<ApiEnvelope<AgentActivityReportPayload>>(`${this.base}/reports/agent-activity`, { params });
  }

  getAgentActivityDetail(
    agentRef: string,
    query: ListAgentActivityQuery = {},
  ): Observable<ApiEnvelope<AgentActivityDetailPayload>> {
    let params = new HttpParams();
    if (query.date_from) params = params.set('date_from', query.date_from);
    if (query.date_to) params = params.set('date_to', query.date_to);
    if (query.page != null) params = params.set('page', String(query.page));
    if (query.page_size != null) params = params.set('page_size', String(query.page_size));
    const ref = encodeURIComponent(agentRef.trim());
    return this.http.get<ApiEnvelope<AgentActivityDetailPayload>>(
      `${this.base}/reports/agent-activity/${ref}`,
      { params },
    );
  }
}
